/**
 * Spec 0038 — the analytics vocabulary shared by the page, the survey card
 * and the student card.
 *
 * Every enum here travels as a plain lowercase String (the project's
 * enum-uppercase→String wire convention), and every human-readable caption
 * is produced HERE, not by the server: the backend's model `choices` are
 * `gettext_lazy`, so asking it for labels makes the API answer in whatever
 * locale the server process happens to sit in.
 */

/** What a question measures, independent of how it is asked. */
export const SURVEY_METRICS = [
  'lesson',
  'teacher',
  'materials',
  'homework',
  'platform',
  'support',
  'nps',
] as const;
export type SurveyMetric = typeof SURVEY_METRICS[number];

/** '' = untagged. Kept as a real option so it can be chosen deliberately. */
export const SURVEY_METRIC_NONE = '';

/** The разрезы `surveyAnalytics` understands. */
export const SURVEY_DIMENSIONS = [
  'course',
  'group',
  'teacher',
  'lesson_number',
  'month',
  'survey',
  'metric',
  'question_type',
] as const;
export type SurveyDimension = typeof SURVEY_DIMENSIONS[number];

/**
 * Dimensions keyed off the ASSIGNMENT. Only these carry roster numbers
 * (eligible / responseRate) and only these can slice a single question —
 * a topic has no roster and cannot split one question.
 */
export const ASSIGNMENT_DIMENSIONS: SurveyDimension[] = [
  'course',
  'group',
  'teacher',
  'lesson_number',
  'month',
  'survey',
];

/**
 * Clicking a row drills one level down. The chain follows how a manager
 * actually narrows a problem: курс → группа → занятие → кто именно.
 */
export const DRILLDOWN_NEXT: Partial<
  Record<SurveyDimension, SurveyDimension>
> = {
  course: 'group',
  group: 'lesson_number',
  month: 'group',
  survey: 'group',
  teacher: 'group',
};

/** Which filter field a row's key lands in when you drill into it. */
export const DRILLDOWN_FILTER_KEY: Partial<
  Record<SurveyDimension, 'courseIds' | 'groupIds' | 'teacherIds' | 'surveyIds'>
> = {
  course: 'courseIds',
  group: 'groupIds',
  teacher: 'teacherIds',
  survey: 'surveyIds',
};

export const SURVEY_SIGNAL_KINDS = [
  'low_rating',
  'low_response_rate',
  'high_negative_share',
  'rating_drop',
  'no_responses',
] as const;
export type SurveySignalKind = typeof SURVEY_SIGNAL_KINDS[number];

export type SurveySentiment = 'negative' | 'neutral' | 'positive' | 'unscored';

/**
 * Mirrors `surveys.services.scoring` and `services.analytics`. Duplicated
 * knowingly and narrowly: these decide a COLOUR, while the backend decides
 * what is actually reported. Any rule that changes behaviour stays server-side.
 */
export const RATING_WEAK_BELOW = 3.5;
export const RATING_STRONG_FROM = 4.5;
export const NEGATIVE_SHARE_WARN_FROM = 25;
export const RESPONSE_RATE_WARN_BELOW = 50;

export type SurveyTone = 'weak' | 'ok' | 'strong' | 'unknown';

export const ratingTone = (average?: number | null): SurveyTone => {
  if (average === null || average === undefined) return 'unknown';
  if (average < RATING_WEAK_BELOW) return 'weak';
  if (average >= RATING_STRONG_FROM) return 'strong';
  return 'ok';
};

export const sentimentTone = (sentiment?: string | null): SurveyTone => {
  if (sentiment === 'negative') return 'weak';
  if (sentiment === 'positive') return 'strong';
  if (sentiment === 'neutral') return 'ok';
  return 'unknown';
};

export interface ISurveyAnalyticsFilter {
  surveyIds?: string[];
  courseIds?: string[];
  groupIds?: string[];
  teacherIds?: string[];
  /**
   * Selects QUESTIONS, not attachments. While set, the response-level
   * numbers (avgScore / negativeShare / eligible / responseRate) come back
   * null — they are computed across ALL of a response's questions and would
   * otherwise answer a different question than the one on screen.
   */
  metrics?: string[];
  planId?: string;
  dateFrom?: string;
  dateTo?: string;
  origin?: string;
  onlyActive?: boolean;
  includeArchivedSurveys?: boolean;
  search?: string;
}

export interface ISurveyAnalyticsRow {
  key?: string | null;
  label: string;
  sortKey: string;
  assignmentsCount?: number | null;
  surveysCount: number;
  responsesCount: number;
  studentsCount: number;
  answersCount: number;
  eligibleCount?: number | null;
  responseRate?: number | null;
  avgScore?: number | null;
  negativeShare?: number | null;
  avgRating?: number | null;
  avgNps?: number | null;
  avgTeacherRating?: number | null;
  yesShare?: number | null;
  lowAnswersShare?: number | null;
}

export interface ISurveySignal {
  kind: string;
  dimension: string;
  key?: string | null;
  label: string;
  value: number;
  previousValue?: number | null;
  delta?: number | null;
  sampleSize: number;
  severity: string;
}

export interface ISurveyComment {
  answerId: string;
  responseId: string;
  questionId: string;
  questionText: string;
  metric: string;
  text: string;
  studentId: string;
  studentName: string;
  groupId: string;
  groupName: string;
  courseName?: string | null;
  surveyId: string;
  surveyTitle: string;
  lessonNumber?: number | null;
  lessonDate?: string | null;
  teacherId?: string | null;
  teacherName?: string | null;
  submittedAt: string;
  score?: number | null;
  sentiment: string;
}

export interface ISurveyAssignmentRow {
  assignmentId: string;
  surveyId: string;
  surveyTitle: string;
  groupId: string;
  groupName: string;
  courseId?: string | null;
  courseName?: string | null;
  lessonId: string;
  lessonName: string;
  lessonNumber: number;
  lessonDate?: string | null;
  teacherId?: string | null;
  teacherName?: string | null;
  isActive: boolean;
  origin: string;
  planId?: string | null;
  createdAt?: string | null;
  respondedCount: number;
  eligibleCount: number;
  responseRate?: number | null;
  averageRating?: number | null;
  avgScore?: number | null;
  negativeShare?: number | null;
}

/**
 * `avgScore` mixes question families with different granularities, so it is
 * only comparable inside ONE survey. The row reports how many it pooled so
 * the table can grey the column out instead of quietly showing a number
 * nobody should compare.
 */
export const scoreIsComparable = (row: ISurveyAnalyticsRow): boolean =>
  row.surveysCount <= 1 && row.avgScore !== null && row.avgScore !== undefined;

/** Percent with one decimal, or the «—» every table in the ERP uses. */
export const formatPercent = (value?: number | null): string =>
  value === null || value === undefined ? '—' : `${value}%`;

export const formatAverage = (value?: number | null): string =>
  value === null || value === undefined ? '—' : value.toFixed(2);
